/**
 * MSAL.js configuration helpers.
 *
 * Per the project rules:
 *   - Use the MSAL library configured for the SPA platform (PKCE + CORS are
 *     enforced by the Entra SPA registration; MSAL adds nothing about that
 *     other than performing the Auth Code + PKCE exchange).
 *   - Do not use localStorage. MSAL requires `sessionStorage` for redirect
 *     flows (`loginRedirect` / `logoutRedirect`): interaction state must
 *     survive the round-trip to Entra. `memoryStorage` throws
 *     `in_mem_redirect_unavailable`. Tab-scoped storage is cleared when the
 *     tab closes. CloudCost auth still lives in the HttpOnly `cc.sid` cookie
 *     after `/api/auth/session`; MSAL cache is only for the OIDC handshake.
 */
import {
  PublicClientApplication,
  type Configuration,
} from '@azure/msal-browser';

export interface SpaIdpConfig {
  name: string;
  authority: string;
  clientId: string;
  scopes: string[];
}

export interface AuthBootstrapConfig {
  idps: SpaIdpConfig[];
  allowLocalLogin: boolean;
  allowLocalPasswordSetup: boolean;
}

export function buildMsalInstance(idp: SpaIdpConfig): PublicClientApplication {
  const redirectUri = `${window.location.origin}/auth/callback`;
  const postLogoutRedirectUri = `${window.location.origin}/`;
  const config: Configuration = {
    auth: {
      clientId: idp.clientId,
      authority: idp.authority,
      redirectUri,
      postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
    system: {
      allowPlatformBroker: false,
    },
  };
  return new PublicClientApplication(config);
}

export interface MsalInstanceEntry {
  idp: SpaIdpConfig;
  instance: PublicClientApplication;
}

export async function buildMsalInstances(
  idps: SpaIdpConfig[],
): Promise<MsalInstanceEntry[]> {
  const entries = idps.map((idp) => ({
    idp,
    instance: buildMsalInstance(idp),
  }));
  await Promise.all(entries.map((e) => e.instance.initialize()));
  return entries;
}
